import { useState, useCallback, useRef } from 'react'
import { getTaskStatus } from '../../../services/api/aiVideoGeneration'

const DEFAULT_POLL_INTERVAL = 2000

/**
 * Custom hook for polling task status
 * @param {Object} options Configuration options
 * @param {number} options.pollInterval Polling interval in ms (default: 2000)
 * @param {boolean} options.continuePollingOnError Whether to keep polling after an API error (default: true)
 */
export const useTaskPoller = ({
  pollInterval = DEFAULT_POLL_INTERVAL,
  continuePollingOnError = true
} = {}) => {
  const [isPolling, setIsPolling] = useState(false)
  const [progress, setProgress] = useState(0)
  const [status, setStatus] = useState(null)
  const [message, setMessage] = useState('')
  const stopPollingRef = useRef(false)

  const stopPolling = useCallback(() => {
    stopPollingRef.current = true
    setIsPolling(false)
  }, [])

  const startPolling = useCallback(
    async taskId => {
      if (!taskId) {
        throw new Error('Task ID is required for polling')
      }

      stopPollingRef.current = false
      setIsPolling(true)
      setProgress(0)
      setStatus('pending')

      return new Promise((resolve, reject) => {
        const poll = async () => {
          if (stopPollingRef.current) {
            resolve({ status: 'cancelled' })
            return
          }

          try {
            const response = await getTaskStatus(taskId)
            const data = response?.data

            if (!data) {
              console.warn('Empty response from task status API')
              setTimeout(poll, pollInterval)
              return
            }

            const currentStatus = data.status
            const currentProgress = data.progress
            const currentMessage = data.message || ''

            setStatus(currentStatus)
            setMessage(currentMessage)

            if (currentProgress) {
              // Handle both distinct formats if necessary, often API returns number or "50%"
              const progressNum =
                typeof currentProgress === 'string'
                  ? parseInt(currentProgress.replace('%', ''))
                  : currentProgress
              setProgress(progressNum)
            }

            if (currentStatus === 'success') {
              setIsPolling(false)
              setStatus('success')
              setProgress(100)
              resolve(data) // Return full data object (usually has 'output' field)
            } else if (
              currentStatus === 'failed' ||
              currentStatus === 'error'
            ) {
              setIsPolling(false)
              setStatus('failed')
              reject(new Error(data.message || 'Task processing failed'))
            } else {
              // Still pending or processing
              setTimeout(poll, pollInterval)
            }
          } catch (error) {
            console.error('Error polling task status:', error)
            // Decide if we want to retry or fail. Usually retry on network error.
            // For now, let's retry unless it's a 4xx error that's not 429
            if (continuePollingOnError) {
              setTimeout(poll, pollInterval)
            } else {
              setIsPolling(false)
              setStatus('failed')
              reject(error)
            }
          }
        }

        poll()
      })
    },
    [pollInterval, continuePollingOnError]
  )

  return {
    isPolling,
    progress,
    status,
    message,
    startPolling,
    stopPolling
  }
}

export default useTaskPoller
